import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  from as apolloFrom,
} from "@apollo/client";
import { RetryLink } from "@apollo/client/link/retry";
import { createPersistedQueryLink } from "@apollo/client/link/persisted-queries";
import { usePregeneratedHashes } from "graphql-codegen-persisted-query-ids/lib/apollo";

import { HTTPErrorCode } from "@caredna-marlin/utils/errorCodes";
import { ORG_ID_HEADER_NAME } from "@caredna-marlin/utils/constants";
import hashes from "../graphql/__generated__/persistedOperations.client.json";

const GRAPHQL_URI =
  import.meta.env.VITE_SERVICE_HOST + import.meta.env.VITE_GRAPHQL_PATH;

type MarlinGraphqlClient = ApolloClient<NormalizedCacheObject>;

export const createMarlinGraphqlClient = (organizationId?: number): MarlinGraphqlClient => {
  return new ApolloClient({
    cache: new InMemoryCache({ addTypename: true }),
    link: createMarlinHttpLink(GRAPHQL_URI, organizationId),
  });
};

const createMarlinHttpLink = (uri: string, organizationId?: number): ApolloLink => {
  const headers = {} as any;

  if (organizationId) {
    headers[ORG_ID_HEADER_NAME] = organizationId;
  }

  const retryLink = new RetryLink({
    attempts: (attempt, _, error) => {
      if (error.statusCode === 401 && error.result?.error === HTTPErrorCode.NotAuthenticated) {
        // The server auto-refreshes, so this means we have no valid refresh token.
        // Therefore, go to the login page.
        window.location.href = "/login";
        return false;
      }

      if (error.statusCode === 403 && error.result?.error === HTTPErrorCode.WrongOrg) {
        delete localStorage.organizationId;
        delete headers[ORG_ID_HEADER_NAME];
        return attempt < 2;
      }

      if (!error.statusCode || error.statusCode >= 500) {
        const retrying = attempt <= 3
        console.log(`Network error detected, attempt ${attempt}. ${retrying ? "Retrying" : "Aborting"}.`);
        return retrying;
      }

      return false;
    },
    delay: {
      initial: 1500,
      jitter: true,
    },
  });

  const persistedLink = createPersistedQueryLink({
    // Not a hook
    // eslint-disable-next-line react-hooks/rules-of-hooks
    generateHash: usePregeneratedHashes(hashes),
  });

  return apolloFrom([persistedLink, retryLink, new HttpLink({ uri, headers, credentials: 'include' })]);
}
